import React from 'react';
import TargetHandle from './TargetHandle';
import classNames from 'classnames';
import './ProgressBar.styl';
import * as ReactDOM from 'react-dom';
import checkFields from 'utils/checkFields';
import debounce from 'lodash/debounce';
import {getMuiV0Theme} from 'utils/css/themeUtils';

export default class ProgressBar extends React.PureComponent {
  constructor() {
    super(...arguments);
    this.state = {
      width: 0,
    };
    this.onResize = debounce(this.onResize.bind(this), 100);
  }

  componentDidMount() {
    window.addEventListener('resize', this.onResize);
    this.onResize();
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.onResize, true);
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    if (checkFields(this.props, prevProps, ['percentage', 'formattedValue'])) {
      this.onResize();
      // XXX The animations on these bars really screw things up, because the width
      // is expanding/shrinking as we check it. So check again after the animation
      // is (mostly) over
      //
      // this however creates an awkward effect where the numbers appear about a second late.
      setTimeout(this.onResize, 800);
    }
  }

  onResize() {
    const node = ReactDOM.findDOMNode(this.barRef);
    if (node) {
      const width = node.clientWidth;
      this.setState({width});
    }
  }

  /**
   * Checks if the entire value will render given the bar's width.
   * Otherwise return an empty string
   *
   * This method of checking text width isn't great, but is more accurate
   * than the previous method.
   */
  getValue() {
    const {width} = this.state;
    const value = this.props.formattedValue && this.props.formattedValue.toString();
    if (!value) return '';

    // These characters are very small, and mess up our width heuristic, so don't measure them
    const filteredText = value.replace(/[:.,]/g, '');
    // Assuming each character is about 12px wide plus a little extra padding
    return width > filteredText.length * 12 + 12 ? value : '';
  }

  render() {
    const {
      percentage: percentageParam,
      // To allow a grab handle the user can use to pick
      // a 'target percentage' (turns this into an user input)
      onSetTarget,
      // initial value for above grab handle
      targetPercentage,
      // Array of additional progress markers (other than the bar itself)
      // each marker being an object of shape: {percentage, renderer}
      markers,
      // Function to call when a progress bar is clicked.
      onClick,
    } = this.props;

    let cappedPct = percentageParam;
    if (percentageParam > 100) {
      console.warn(
        'Percentage is over 100! Capping. This may be a bug if not a transient loading state'
      );
      cappedPct = 100;
    }

    if (isNaN(cappedPct)) {
      cappedPct = 0;
    }

    const percentage = cappedPct;
    const style = {
      width: `${percentage}%`,
      backgroundColor: this.props.color,
    };

    return (
      <div className={classNames('progress-bar-container', this.props.className)} onClick={onClick}>
        <div className="progress-bar">
          {onSetTarget && (
            <TargetHandle
              percentage={targetPercentage}
              color={getMuiV0Theme().palette.flamingo}
              onSetTarget={onSetTarget}
            />
          )}
          <div className="bar">
            <div className="percentage-container">
              <div className="percentage" ref={ref => (this.barRef = ref)} style={style}>
                <div className="value" style={{backgroundColor: this.props.color}}>
                  {this.getValue()}
                </div>
              </div>
            </div>
          </div>
          <div className="progress-markers">
            {markers
              ? markers.map((marker, i) => (
                  <div
                    key={i}
                    className="progress-marker"
                    style={{
                      left: `${marker.percentage}%`,
                    }}>
                    <marker.renderer />
                  </div>
                ))
              : null}
          </div>
        </div>
      </div>
    );
  }
}
